import { useState } from 'react';
import { supabase } from '../../../utils/supabaseClient';

export interface VerificationFailure {
  field: string;
  url: string;
  reason: string;
  status?: number;
}

export interface ToolExecution {
  id?: string;
  name: string;
  duration_ms: number;
  success: boolean;
  error?: string;
}

export interface IterationSnapshot {
  iteration: number;
  actions: string[];
  findings: string[];
}

export interface UrlDiscoveryResult {
  status: 'needs_review' | 'completed';
  discovered_urls: Record<string, string | null>;
  verified_urls: Record<string, string | null>;
  verification_failures: VerificationFailure[];
  qc_flag_media: boolean;
  qc_flag_media_notes: string | null;
  submission_type?: {
    type: string;
    eligibility: string;
  };
  recommendation: 'AUTO_CONTINUE' | 'REVIEW_URLS' | 'INSUFFICIENT_DATA' | 'MARK_INELIGIBLE';
  iteration_history: IterationSnapshot[];
  tool_executions: ToolExecution[];
  url_count: number;
}

export function useUrlDiscovery() {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<UrlDiscoveryResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const discoverUrls = async (programId: string, baciCode: string | null) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: rpcError } = await supabase.functions.invoke('url-discovery', {
        body: {
          program_id: programId,
          baci_code: baciCode ?? undefined
        }
      });

      if (rpcError) {
        throw new Error(rpcError.message);
      }

      if (!data || data.status === 'error') {
        throw new Error(data?.error ?? 'URL discovery failed');
      }

      setResult(data as UrlDiscoveryResult);
      return data as UrlDiscoveryResult;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'URL discovery failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    discoverUrls,
    isLoading,
    result,
    error,
    reset: () => {
      setResult(null);
      setError(null);
    }
  };
}
